import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Baby, DollarSign, MessageSquare, FileText, Clock, Heart, Newspaper, Calendar, ChevronRight, X, CreditCard, Loader2, Building2, Settings, Bell, Mail, CheckCircle, LogIn, LogOut
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';

import API from '@/config/api';
import ParentRemindersBanner from '@/components/ParentRemindersBanner';
// API imported from config;

const ParentDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const [children, setChildren] = useState([]);
  const [reports, setReports] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ open: false, invoice: null });
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processingFee, setProcessingFee] = useState(0);
  
  // Email preferences state
  const [emailPrefsOpen, setEmailPrefsOpen] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({
    weekly_summary: true,
    check_in_alerts: true,
    check_out_alerts: true,
    payment_reminders: true,
    daily_reports: true
  });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchEmailPreferences();
  }, []);

  const fetchEmailPreferences = async () => {
    try {
      const response = await axios.get(`${API}/auth/email-preferences`, getAuthHeaders());
      setEmailPrefs(response.data);
    } catch (error) {
      console.error('Failed to load email preferences');
    }
  };

  const saveEmailPreferences = async () => {
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/auth/email-preferences`, emailPrefs, getAuthHeaders());
      toast.success('Email preferences saved');
      setEmailPrefsOpen(false);
    } catch (error) {
      toast.error('Failed to save email preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [childrenRes, reportsRes, invoicesRes, newslettersRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/children`, getAuthHeaders()),
        axios.get(`${API}/daily-reports`, getAuthHeaders()),
        axios.get(`${API}/payments/parent/invoices`, getAuthHeaders()).catch(() => axios.get(`${API}/invoices`, getAuthHeaders())),
        axios.get(`${API}/newsletters/parent/list`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/parent/notifications`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setChildren(childrenRes.data);
      setReports(reportsRes.data.slice(0, 5));
      setInvoices(invoicesRes.data.filter(i => i.status === 'pending' || i.status === 'overdue' || i.status === 'partial'));
      setNewsletters(newslettersRes.data.slice(0, 3));
      setNotifications(notificationsRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateFee = (amount, method) => {
    const amt = parseFloat(amount) || 0;
    if (method === 'ach') {
      return 1.00; // $1 flat fee
    } else {
      return Math.round(amt * 0.0275 * 100) / 100; // 2.75%
    }
  };

  const openPaymentModal = (invoice) => {
    const balanceDue = (invoice.amount || 0) - (invoice.amount_paid || 0);
    setCustomAmount(balanceDue.toFixed(2));
    setPaymentMethod('card');
    setProcessingFee(calculateFee(balanceDue, 'card'));
    setPaymentModal({ open: true, invoice });
  };

  const handleAmountChange = (value) => {
    setCustomAmount(value);
    setProcessingFee(calculateFee(value, paymentMethod));
  };

  const handleMethodChange = (method) => {
    setPaymentMethod(method);
    setProcessingFee(calculateFee(customAmount, method));
  };

  const handlePayInvoice = async (invoiceId, amountToPay = null, method = 'card') => {
    setPayingInvoice(invoiceId);
    setPaymentModal({ open: false, invoice: null });
    try {
      const payload = {
        invoice_id: invoiceId,
        return_url: window.location.href,
        payment_method: method
      };
      
      // If a specific amount is provided, include it for partial payment
      if (amountToPay !== null) {
        payload.amount = parseFloat(amountToPay);
      }
      
      const res = await axios.post(
        `${API}/payments/parent/pay`,
        payload,
        getAuthHeaders()
      );
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment not available. Please contact the center.');
      setPayingInvoice(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Parent Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Parent Dashboard">
      <div className="space-y-8" data-testid="parent-dashboard">
        {/* Reminders banner — health reports, pending forms */}
        <ParentRemindersBanner />

        {/* Welcome Card */}
        <Card className="card-clay bg-gradient-to-r from-primary/10 to-primary/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl">Welcome back, {user?.name}!</h2>
                <p className="text-muted-foreground">Here's what's happening with your family today.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Children Cards */}
        <div>
          <h3 className="font-heading font-bold text-lg mb-4">My Children</h3>
          {children.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {children.map((child) => (
                <Card key={child.id} className="card-clay" data-testid={`child-card-${child.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      {child.photo_url ? (
                        <img
                          src={`${API}${child.photo_url}`.replace('/api/api/', '/api/')}
                          alt={`${child.first_name} ${child.last_name}`}
                          className="w-14 h-14 rounded-full object-cover"
                          data-testid={`parent-child-photo-${child.id}`}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-bold text-xl text-primary">
                            {child.first_name.charAt(0)}{child.last_name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-lg">{child.first_name} {child.last_name}</p>
                        <p className="text-sm text-muted-foreground">{child.classroom}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        child.is_checked_in 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {child.is_checked_in ? 'At Center' : 'Not Checked In'}
                      </span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => window.location.href = '/children'}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-clay">
              <CardContent className="p-8 text-center">
                <Baby className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">No children registered yet</p>
                <Button onClick={() => window.location.href = '/children'}>
                  Register Child
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Check-In/Out Notifications */}
        {notifications.length > 0 && (
          <Card className="card-clay border-l-4 border-l-primary" data-testid="parent-notifications">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Recent Activity
                {notifications.filter(n => !n.is_read).length > 0 && (
                  <Badge className="bg-red-500 text-white text-xs">
                    {notifications.filter(n => !n.is_read).length} new
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {notifications.slice(0, 10).map((notif) => (
                  <div key={notif.id} className={`flex items-center gap-3 p-3 rounded-lg border ${!notif.is_read ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      notif.type === 'check_in' ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      {notif.type === 'check_in' 
                        ? <LogIn className="w-4 h-4 text-green-600" />
                        : <LogOut className="w-4 h-4 text-orange-600" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{notif.title}</p>
                        {!notif.is_read && <Badge className="bg-primary text-[10px] h-4 px-1">New</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{notif.message}</p>
                      {notif.provider_name && (
                        <p className="text-[10px] text-muted-foreground">
                          Verified by: <span className="font-medium">{notif.provider_name}</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-muted-foreground">
                        {new Date(notif.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Reports */}
          <Card className="card-clay">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Recent Reports
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => window.location.href = '/daily-reports'}>
                View All
              </Button>
            </CardHeader>
            <CardContent>
              {reports.length > 0 ? (
                <div className="space-y-3">
                  {reports.map((report) => (
                    <div 
                      key={report.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-xl cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => window.location.href = '/daily-reports'}
                    >
                      <div>
                        <p className="font-medium">{report.child_name}</p>
                        <p className="text-sm text-muted-foreground">{report.date}</p>
                      </div>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        {report.mood}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No reports yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Payments */}
          <Card className="card-clay">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Pending Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="space-y-3">
                  {invoices.map((invoice) => {
                    const totalAmount = invoice.amount || 0;
                    const amountPaid = invoice.amount_paid || 0;
                    const balanceDue = totalAmount - amountPaid;
                    const hasPartialPayment = amountPaid > 0;
                    
                    return (
                      <div 
                        key={invoice.id}
                        className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl"
                        data-testid={`invoice-card-${invoice.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-medium">{invoice.description || `${invoice.invoice_type} - ${invoice.child_name}`}</p>
                            <p className="text-sm text-muted-foreground">
                              Due: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                            </p>
                            {hasPartialPayment && (
                              <div className="mt-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full inline-block">
                                ${amountPaid.toFixed(2)} paid
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            {hasPartialPayment ? (
                              <>
                                <p className="text-xs text-muted-foreground line-through">${totalAmount.toFixed(2)}</p>
                                <p className="font-bold text-xl text-amber-700">${balanceDue.toFixed(2)}</p>
                                <p className="text-xs text-muted-foreground">remaining</p>
                              </>
                            ) : (
                              <p className="font-bold text-xl text-amber-700">${totalAmount.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                            onClick={() => handlePayInvoice(invoice.id)}
                            disabled={payingInvoice === invoice.id}
                            data-testid={`pay-full-btn-${invoice.id}`}
                          >
                            {payingInvoice === invoice.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-1" />
                            )}
                            Pay ${balanceDue.toFixed(2)}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-amber-300 text-amber-700 hover:bg-amber-100"
                            onClick={() => openPaymentModal(invoice)}
                            disabled={payingInvoice === invoice.id}
                            data-testid={`pay-custom-btn-${invoice.id}`}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            Custom
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="w-16 h-16 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                  <p className="font-medium text-green-700">All caught up!</p>
                  <p className="text-sm">No pending payments</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Newsletters Section */}
        {newsletters.length > 0 && (
          <Card className="overflow-hidden border-0 shadow-lg">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
              <CardTitle className="flex items-center gap-2 text-white">
                <Newspaper className="w-5 h-5" />
                Latest Newsletters
              </CardTitle>
            </div>
            <CardContent className="p-0">
              <div className="divide-y">
                {newsletters.map((newsletter) => (
                  <div 
                    key={newsletter.id} 
                    className="p-5 hover:bg-emerald-50 transition-colors cursor-pointer group"
                    onClick={() => setSelectedNewsletter(newsletter)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-lg">
                            📰
                          </div>
                          <div>
                            <h4 className="font-semibold text-lg group-hover:text-emerald-600 transition-colors">
                              {newsletter.title}
                            </h4>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {newsletter.published_at && new Date(newsletter.published_at).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 ml-13 pl-13">
                          {newsletter.content.split('\n')[0]}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Newsletter Modal */}
        <Dialog open={!!selectedNewsletter} onOpenChange={() => setSelectedNewsletter(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0">
            {selectedNewsletter && (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-6 text-white">
                  <DialogTitle className="text-2xl font-bold mb-2">
                    {selectedNewsletter.title}
                  </DialogTitle>
                  <p className="text-white/80 text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {selectedNewsletter.published_at && new Date(selectedNewsletter.published_at).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  {selectedNewsletter.content.split('\n\n---\n\n').map((section, idx) => {
                    const lines = section.split('\n');
                    const header = lines[0];
                    const content = lines.slice(1).join('\n');
                    
                    // Detect section type by emoji
                    let bgColor = 'bg-gray-50';
                    let borderColor = 'border-gray-200';
                    if (header.includes('📢')) { bgColor = 'bg-blue-50'; borderColor = 'border-blue-200'; }
                    else if (header.includes('📅')) { bgColor = 'bg-purple-50'; borderColor = 'border-purple-200'; }
                    else if (header.includes('⏰')) { bgColor = 'bg-amber-50'; borderColor = 'border-amber-200'; }
                    else if (header.includes('🎉')) { bgColor = 'bg-green-50'; borderColor = 'border-green-200'; }
                    else if (header.includes('🏥')) { bgColor = 'bg-red-50'; borderColor = 'border-red-200'; }
                    else if (header.includes('📚')) { bgColor = 'bg-indigo-50'; borderColor = 'border-indigo-200'; }
                    else if (header.includes('📸')) { bgColor = 'bg-pink-50'; borderColor = 'border-pink-200'; }
                    
                    return (
                      <div key={idx} className={`mb-4 p-4 rounded-lg ${bgColor} border ${borderColor}`}>
                        <h3 className="font-semibold text-lg mb-2">{header}</h3>
                        <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {content}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Footer */}
                <div className="px-6 pb-6">
                  <Button 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                    onClick={() => setSelectedNewsletter(null)}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Partial Payment Modal */}
        <Dialog open={paymentModal.open} onOpenChange={(open) => !open && setPaymentModal({ open: false, invoice: null })}>
          <DialogContent className="sm:max-w-md" data-testid="partial-payment-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Make a Payment
              </DialogTitle>
              <DialogDescription>
                Enter the amount you'd like to pay towards this invoice.
              </DialogDescription>
            </DialogHeader>
            
            {paymentModal.invoice && (
              <div className="space-y-4 py-4">
                {/* Invoice Details */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium">{paymentModal.invoice.description || `${paymentModal.invoice.invoice_type} - ${paymentModal.invoice.child_name}`}</p>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Amount:</span>
                    <span className="font-medium">${(paymentModal.invoice.amount || 0).toFixed(2)}</span>
                  </div>
                  {(paymentModal.invoice.amount_paid || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Already Paid:</span>
                      <span className="font-medium text-green-600">${(paymentModal.invoice.amount_paid || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t mt-2 pt-2">
                    <span className="font-medium">Balance Due:</span>
                    <span className="font-bold text-amber-700">
                      ${((paymentModal.invoice.amount || 0) - (paymentModal.invoice.amount_paid || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {/* Amount Input */}
                <div className="space-y-2">
                  <Label htmlFor="payment-amount">Payment Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="payment-amount"
                      type="number"
                      min="1"
                      max={((paymentModal.invoice.amount || 0) - (paymentModal.invoice.amount_paid || 0))}
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      className="pl-7"
                      placeholder="0.00"
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Minimum payment: $1.00</p>
                </div>

                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <Label>Payment Method</Label>
                  <RadioGroup 
                    value={paymentMethod} 
                    onValueChange={handleMethodChange}
                    className="space-y-2"
                  >
                    <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${paymentMethod === 'card' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'}`}>
                      <RadioGroupItem value="card" id="card" />
                      <Label htmlFor="card" className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-primary" />
                            <span className="font-medium">Debit or Credit Card</span>
                          </div>
                          <span className="text-sm text-muted-foreground">2.75% fee</span>
                        </div>
                      </Label>
                    </div>
                    <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${paymentMethod === 'ach' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'}`}>
                      <RadioGroupItem value="ach" id="ach" />
                      <Label htmlFor="ach" className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-primary" />
                            <span className="font-medium">Bank Transfer (ACH)</span>
                          </div>
                          <span className="text-sm text-muted-foreground">$1.00 fee</span>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Fee Breakdown */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Invoice Amount:</span>
                    <span>${customAmount ? parseFloat(customAmount).toFixed(2) : '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Processing Fee ({paymentMethod === 'ach' ? '$1.00' : '2.75%'}):</span>
                    <span>${processingFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base border-t mt-2 pt-2">
                    <span>Total Charge:</span>
                    <span className="text-primary">${(parseFloat(customAmount || 0) + processingFee).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setPaymentModal({ open: false, invoice: null })}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handlePayInvoice(paymentModal.invoice.id, customAmount, paymentMethod)}
                disabled={!customAmount || parseFloat(customAmount) < 1 || payingInvoice === paymentModal.invoice?.id}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                data-testid="confirm-payment-btn"
              >
                {payingInvoice === paymentModal.invoice?.id ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : paymentMethod === 'ach' ? (
                  <Building2 className="w-4 h-4 mr-1" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-1" />
                )}
                Pay ${(parseFloat(customAmount || 0) + processingFee).toFixed(2)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            variant="outline" 
            className="h-auto py-6 flex flex-col items-center gap-2 card-clay"
            onClick={() => window.location.href = '/daily-reports'}
          >
            <FileText className="w-6 h-6 text-primary" />
            <span>Daily Reports</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-6 flex flex-col items-center gap-2 card-clay"
            onClick={() => window.location.href = '/messages'}
          >
            <MessageSquare className="w-6 h-6 text-primary" />
            <span>Messages</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-6 flex flex-col items-center gap-2 card-clay"
            onClick={() => window.location.href = '/billing'}
          >
            <DollarSign className="w-6 h-6 text-primary" />
            <span>Payments</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-6 flex flex-col items-center gap-2 card-clay"
            onClick={() => setEmailPrefsOpen(true)}
            data-testid="email-prefs-btn"
          >
            <Mail className="w-6 h-6 text-primary" />
            <span>Email Settings</span>
          </Button>
        </div>

        {/* Email Preferences Dialog */}
        <Dialog open={emailPrefsOpen} onOpenChange={setEmailPrefsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Email Notification Settings
              </DialogTitle>
              <DialogDescription>
                Choose which email notifications you'd like to receive
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-sm">Weekly Summary</p>
                    <p className="text-xs text-muted-foreground">Get a summary of your child's week every Friday</p>
                  </div>
                </div>
                <Switch
                  checked={emailPrefs.weekly_summary}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, weekly_summary: checked }))}
                  data-testid="weekly-summary-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-sm">Check-in Alerts</p>
                    <p className="text-xs text-muted-foreground">Get notified when your child is checked in</p>
                  </div>
                </div>
                <Switch
                  checked={emailPrefs.check_in_alerts}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, check_in_alerts: checked }))}
                  data-testid="checkin-alerts-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-sm">Check-out Alerts</p>
                    <p className="text-xs text-muted-foreground">Get notified when your child is checked out</p>
                  </div>
                </div>
                <Switch
                  checked={emailPrefs.check_out_alerts}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, check_out_alerts: checked }))}
                  data-testid="checkout-alerts-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-sm">Payment Reminders</p>
                    <p className="text-xs text-muted-foreground">Reminders about upcoming or due payments</p>
                  </div>
                </div>
                <Switch
                  checked={emailPrefs.payment_reminders}
                  onCheckedChange={(checked) => setEmailPrefs(prev => ({ ...prev, payment_reminders: checked }))}
                  data-testid="payment-reminders-toggle"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailPrefsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveEmailPreferences} disabled={savingPrefs} data-testid="save-email-prefs-btn">
                {savingPrefs ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    Saving...
                  </>
                ) : (
                  'Save Preferences'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentDashboard;
